import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FunnelIcon } from "../utils/icons";
import { products } from "../data/products";
import ProductCard from "../components/ui/ProductCard";

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || "all"
  );
  const [sortBy, setSortBy] = useState("featured");
  const [showFilters, setShowFilters] = useState(false);

  const categories = ["all", ...new Set(products.map((p) => p.category))];

  useEffect(() => {
    let result = [...products];
    const filterParam = searchParams.get("filter");

    // Handle bestseller filter
    if (filterParam === "bestseller") {
      result = result.filter((p) => p.featured);
    } else {
      // Handle category filter
      if (selectedCategory !== "all") {
        result = result.filter(
          (p) => p.category.toLowerCase() === selectedCategory.toLowerCase()
        );
      }
    }

    // Apply sorting
    switch (sortBy) {
      case "price-low":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        result.sort((a, b) => b.price - a.price);
        break;
      case "name":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        result.sort((a, b) => (b.featured ? 1 : -1));
    }

    setFilteredProducts(result);
  }, [selectedCategory, sortBy, searchParams]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === "all") {
      searchParams.delete("category");
      searchParams.delete("filter"); // Remove bestseller filter when changing category
    } else {
      searchParams.set("category", category);
      searchParams.delete("filter"); // Remove bestseller filter when changing category
    }
    setSearchParams(searchParams);
  };

  // Get page title based on filter
  const getPageTitle = () => {
    const filterParam = searchParams.get("filter");
    if (filterParam === "bestseller") {
      return "Best Sellers";
    }
    return "Our Products";
  };

  return (
    <div className="bg-gray-50 min-h-screen pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <h1 className="text-3xl font-serif font-bold text-primary-900 mb-4 md:mb-0">
            {getPageTitle()}
          </h1>
          <button
            className="md:hidden flex items-center text-gray-600 mb-4"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
          <div
            className={`${
              showFilters ? "block" : "hidden"
            } md:flex space-y-4 md:space-y-0 md:space-x-4`}
          >
            {!searchParams.get("filter") && (
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full md:w-auto px-4 py-2 rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            )}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full md:w-auto px-4 py-2 rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ProductCard product={product} />
            </motion.div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No products found.</p>
          </div>
        )}
      </div>
    </div>
  );
}
